import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'
import { LOGO_URL, SITE_HOST, cdnUrl, siteEmail, siteUrl } from '@/lib/config/site'

/**
 * The canonical submission template for the production site. Derived from analyzing 13
 * live directory profiles (Trustpilot, SourceForge, Wellfound, Crunchbase,
 * dang.ai, TAAFT, SaaSHub, Toolify, DomainInvesting, Uneed, AIToolsTime,
 * Tyy.ai, SeekTool). Fields are ordered roughly by how often a directory asks
 * for them; the right column shows the frequency tally.
 */

type Field = {
  key: string
  label: string
  value: string
  hint?: string
  /** How many of the 13 audited live directories asked for this field. */
  frequency: number
  multiline?: boolean
}

const FIELDS: Field[] = [
  {
    key: 'name',
    label: 'Product name',
    value: 'Footage',
    frequency: 13,
  },
  {
    key: 'url',
    label: 'Website URL',
    value: siteUrl(),
    frequency: 13,
  },
  {
    key: 'tagline',
    label: 'Tagline (short)',
    value: 'AI video agent that writes briefs, generates clips, and edits video so you can focus on your creative vision.',
    hint: '160 chars max. Used as the one-line subhead under product name.',
    frequency: 13,
  },
  {
    key: 'short_description',
    label: 'Short description',
    value: 'Footage is an all-in-one autonomous AI video agent. From a single prompt it writes the brief, generates the clips, and edits the final video — built for creators of every skill level.',
    hint: '200-300 chars. Used in card descriptions and listing previews.',
    frequency: 13,
    multiline: true,
  },
  {
    key: 'long_description',
    label: 'Long description',
    value: `Footage makes it simple to create, edit, and share high-quality videos with AI. Designed for creators of all skill levels, Footage handles the technical complexity so you can focus on storytelling, creativity, and bringing your ideas to life with ease.

Describe what you want in plain English, drop in a reference image, and Footage's agent writes a shot-by-shot brief, generates each clip, and assembles the cuts into a polished video — narration, captions, transitions, and music included. Iterate on any clip in seconds and export the project as MP4 ready for any platform.

Footage is built for marketing teams, content creators, educators, and product builders who need professional video output without learning a traditional editor. Start free at ${SITE_HOST} — no credit card required.`,
    hint: '500-3000 chars / 3-5 paragraphs. Used on detail pages, full profiles, and review articles.',
    frequency: 9,
    multiline: true,
  },
  {
    key: 'logo',
    label: 'Logo URL',
    value: LOGO_URL,
    hint: 'Square PNG, transparent background. Most directories accept SVG/JPG too.',
    frequency: 12,
  },
  {
    key: 'screenshot_home',
    label: 'Homepage screenshot',
    value: cdnUrl('/brand/screenshot-homepage.png'),
    hint: 'Marketing hero image (2548×1028). Crop to whatever aspect the directory wants.',
    frequency: 10,
  },
  {
    key: 'screenshot_app',
    label: 'App / dashboard screenshot',
    value: cdnUrl('/brand/screenshot-dashboard.jpg'),
    hint: 'In-product shot showing the editor and generated clips.',
    frequency: 8,
  },
  {
    key: 'categories',
    label: 'Categories / tags',
    value: 'AI Video Generator, AI Video Editor, Text to Video, Image to Video, Content Creation, AI Agents, Video Tools',
    hint: 'Pick 3-5 max per directory. AI Video Generator is the primary on every site.',
    frequency: 13,
  },
  {
    key: 'pricing_model',
    label: 'Pricing model',
    value: 'Freemium (Free + paid tiers)',
    frequency: 13,
  },
  {
    key: 'pricing_tiers',
    label: 'Pricing tiers',
    value: `Free — $0/mo. Create projects with AI, manage content, basic support.
Pro — $9.99/mo. All AI models, standard usage, 7-day free trial.
Max — $39.99/mo. High-volume capacity, all AI models, priority support.`,
    hint: 'Toolify, SaaSHub, AIToolsTime, dang.ai all surface this verbatim.',
    frequency: 11,
    multiline: true,
  },
  {
    key: 'platforms',
    label: 'Platforms supported',
    value: 'Web (Cloud)',
    frequency: 7,
  },
  {
    key: 'use_cases',
    label: 'Use cases (5 bullets)',
    value: `1. Marketing campaigns — product launches, social ads, promo videos
2. Content creators — YouTube intros, Reels, TikToks, shorts
3. Education & training — explainer videos, course material, onboarding
4. Internal communications — company updates, announcements, demos
5. Product demos — feature walkthroughs and case studies`,
    frequency: 8,
    multiline: true,
  },
  {
    key: 'key_features',
    label: 'Key features (4-5 bullets)',
    value: `• AI agent writes the brief, generates the clips, and edits the cut for you
• Text-to-video, image-to-video, and video-to-video in one timeline
• Access to the latest AI video generators in a single workspace
• Built-in narration, captions, music, and 16:9 / 9:16 / 1:1 exports
• Iterate any clip in seconds without redoing the whole project`,
    frequency: 9,
    multiline: true,
  },
  {
    key: 'company_name',
    label: 'Company / legal name',
    value: 'Generative Platforms Inc.',
    frequency: 6,
  },
  {
    key: 'founders',
    label: 'Founders',
    value: 'Armen Petrosian (CEO), Sandra De Castro (CTO)',
    frequency: 5,
  },
  {
    key: 'founded',
    label: 'Founded',
    value: 'December 2024',
    frequency: 6,
  },
  {
    key: 'hq',
    label: 'Headquarters',
    value: 'San Diego, California, United States',
    frequency: 5,
  },
  {
    key: 'team_size',
    label: 'Team size',
    value: '1–10',
    frequency: 3,
  },
  {
    key: 'funding',
    label: 'Total funding',
    value: '$2.2M (Pre-Seed Apr 2026; Angel Dec 2025)',
    hint: 'Crunchbase / Wellfound / investor directories ask for this.',
    frequency: 3,
  },
  {
    key: 'contact_email',
    label: 'Contact email',
    value: siteEmail('contact'),
    frequency: 8,
  },
  {
    key: 'support_url',
    label: 'Support URL',
    value: siteUrl('/support'),
    frequency: 4,
  },
  {
    key: 'login_url',
    label: 'Login URL',
    value: siteUrl('/login'),
    frequency: 3,
  },
  {
    key: 'pricing_url',
    label: 'Pricing URL',
    value: siteUrl('/pricing'),
    frequency: 4,
  },
  {
    key: 'twitter',
    label: 'Twitter / X',
    value: 'https://twitter.com/footageai',
    frequency: 9,
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    value: 'https://www.linkedin.com/company/footage-com',
    frequency: 7,
  },
  {
    key: 'founder_twitter',
    label: "Founder's Twitter",
    value: 'https://twitter.com/armen-petrosian',
    frequency: 4,
  },
]

function CopyBtn({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={async () => {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      }}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition',
        copied
          ? 'border-emerald-600/30 bg-emerald-50 text-emerald-700'
          : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-400 hover:text-neutral-900'
      )}
      style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

export default function AdminDirectoryTemplatePage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  if (!isAdmin) return <div className="p-8 text-sm text-neutral-500">Admin only.</div>

  const fullText = FIELDS.map(f => `## ${f.label}\n${f.value}`).join('\n\n')

  return (
    <div className="min-h-screen bg-[#fafaf7]" style={{ fontFamily: '"Crimson Text", Georgia, serif' }}>
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-8 sm:pt-12">
        <Link
          to="/admin/directories"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-neutral-500 hover:text-neutral-900"
          style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Submit queue
        </Link>

        <header className="mt-6 mb-10 sm:mt-8">
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
            Reusable submission template
          </p>
          <h1 className="mt-2 text-[32px] leading-[1.05] tracking-tight text-neutral-900 sm:text-[44px]" style={{ fontFamily: '"DM Serif Display", Georgia, serif' }}>
            Directory submission template
          </h1>
          <p className="mt-3 max-w-prose text-sm leading-relaxed text-neutral-600 sm:text-base">
            Pre-filled answers for the fields directories ask for, derived from auditing 13 live {SITE_HOST} profiles. Copy and paste — the <span className="font-semibold">frequency</span> column shows how many of the 13 directories surface that field, so you know what to prepare first.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <CopyBtn value={fullText} />
            <span className="text-[11px] text-neutral-400" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
              Copy everything as one block
            </span>
          </div>
        </header>

        <dl className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key} className="rounded-xl border border-neutral-200/70 bg-white p-5 shadow-[0_1px_0_rgba(0,0,0,0.02)]">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[15px] font-medium text-neutral-900 sm:text-base" style={{ fontFamily: '"DM Serif Display", Georgia, serif' }}>
                  {f.label}
                </dt>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px] tabular-nums text-neutral-600"
                    style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}
                    title={`Asked by ${f.frequency} of 13 live directories`}
                  >
                    {f.frequency}/13
                  </span>
                  <CopyBtn value={f.value} />
                </div>
              </div>
              {f.hint && (
                <p className="mt-1.5 text-[12px] text-neutral-500" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
                  {f.hint}
                </p>
              )}
              <dd
                className={cn(
                  'mt-3 rounded-md border border-neutral-100 bg-[#fbfaf6] px-3.5 py-2.5 text-[14px] leading-relaxed text-neutral-800',
                  f.multiline && 'whitespace-pre-wrap'
                )}
                style={{ fontFamily: f.multiline ? 'inherit' : '"JetBrains Mono", ui-monospace, monospace', fontSize: f.multiline ? undefined : 12 }}
              >
                {f.value}
              </dd>
            </div>
          ))}
        </dl>

        <section className="mt-12">
          <h2 className="text-[22px] tracking-tight text-neutral-900" style={{ fontFamily: '"DM Serif Display", Georgia, serif' }}>
            Coverage map
          </h2>
          <p className="mt-2 text-sm text-neutral-600">Which directory asked for which field, so you can pre-trim for each.</p>
          <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 text-[12px] text-neutral-600 sm:grid-cols-3" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
            {[
              ['Crunchbase', 'company, founders, funding, HQ, team size'],
              ['Wellfound', 'company, founders, hiring, stage'],
              ['SaaSHub', 'tagline, description, pricing, Q&A, screenshots'],
              ['Toolify', 'features, use cases, pricing tiers, traffic'],
              ['TAAFT', 'tagline, inputs/outputs, tags, pricing'],
              ['SourceForge', 'audience, description, alternatives, support'],
              ['dang.ai', 'tagline, FAQ, target audience, pricing'],
              ['Trustpilot', 'description, support'],
              ['DomainInvesting', 'editorial mention only'],
              ['Uneed', 'tagline, launch date, badge'],
              ['AIToolsTime', 'description, pricing, plan tier'],
              ['Tyy.ai', 'long description, features, use cases, FAQ'],
              ['SeekTool', 'tagline, features, use cases, how-to-steps'],
            ].map(([dir, fields]) => (
              <li key={dir as string}>
                <span className="text-neutral-900">{dir}</span> <span className="text-neutral-400">— {fields}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
